<script setup lang="ts">
/**
 * Shell do App da Clínica — porte de `ClinicApp` (Docs/V2/Clinica/clinica/app.jsx).
 *
 * A MESMA árvore de rotas nos dois formatos; muda só o chrome:
 *   celular  → `.app.m` + `<RouterView>` + bottom-nav de 5 abas
 *   desktop  → `.app.d` + `.deskwrap` [rail 236px | `.deskmain` com `<RouterView>`]
 *
 * Não entram, por serem andaime de demonstração do protótipo: `.hostbar` (barra com alternador
 * Celular/Desktop e tema) e a moldura `ios-frame`. O formato vem da largura real da janela, e o
 * ambiente é decidido em build time — nenhuma tela tem seletor de ambiente (regra #24).
 */
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { navigableDestinations, type BadgeKey } from '@/router/destinations';
import { useSessionStore } from '@/stores/session.store';
import { usePortalStore } from '@/stores/portal.store';
import ClinicBottomNav from './ClinicBottomNav.vue';
import ClinicRail from './ClinicRail.vue';
import { useShellMode } from './useShellMode';
import DtInstallBanner from '@/design-system/components/DtInstallBanner.vue';

const route = useRoute();
const mode = useShellMode();
const { activeCompany } = useSessionStore();
const portal = usePortalStore();

/**
 * Destino aceso: `meta.activeDestination` quando a rota é filha (`/plano/proposta` acende
 * "Meu plano"), senão o próprio nome da rota.
 */
const activeName = computed<string | null>(() => {
  const declared = route.meta.activeDestination;
  if (typeof declared === 'string') return declared;
  return typeof route.name === 'string' ? route.name : null;
});

/**
 * Contadores da navegação. Vêm de `GET /portal-summary` (`Badges{Maintenances,Plan,Clinic}`) e
 * NUNCA de contagem feita no cliente — quem conta e quem ordena é o servidor. Sem resumo
 * carregado o shell não inventa número: mapa vazio == sem contador.
 */
const badges = computed<Readonly<Partial<Record<BadgeKey, number>>>>(() => {
  if (portal.summary.kind !== 'data') return {};
  return {
    maintenances: portal.summary.value.badges.maintenances,
    plan: portal.summary.value.badges.plan,
    clinic: portal.summary.value.badges.clinic,
  };
});

const railFooter = computed<string | null>(() => activeCompany.value?.name ?? null);

// Multi-clínica: o resumo é por unidade — recarrega na troca e descarta o da anterior.
watch(
  () => activeCompany.value?.id,
  (companyId) => {
    if (companyId === undefined) {
      portal.reset();
      return;
    }
    void portal.load(companyId);
  },
  { immediate: true },
);

// Polling de 60s enquanto o shell estiver montado (equivale ao "só com listeners" do Flutter),
// e só com a aba visível — sem consulta em segundo plano.
let pollTimer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  pollTimer = setInterval(() => {
    const companyId = activeCompany.value?.id;
    if (companyId !== undefined && document.visibilityState === 'visible') {
      void portal.load(companyId);
    }
  }, 60_000);
});
onUnmounted(() => {
  if (pollTimer !== null) clearInterval(pollTimer);
});

</script>

<template>
  <div
    class="app"
    :class="mode"
  >
    <div class="shell-layout">
      <ClinicRail
        v-if="mode === 'd'"
        :items="navigableDestinations"
        :badges="badges"
        :active-name="activeName"
        :footer="railFooter"
      />
      <main class="shell-main">
        <DtInstallBanner />
        <RouterView v-slot="{ Component }">
          <Transition
            name="page-fade"
            mode="out-in"
          >
            <component :is="Component" />
          </Transition>
        </RouterView>
      </main>
    </div>
    <ClinicBottomNav
      v-if="mode === 'm'"
      :items="navigableDestinations"
      :badges="badges"
      :active-name="activeName"
    />
  </div>
</template>

<style scoped>
.shell-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}

.shell-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

/* Transição suave e sutil entre rotas */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.15s ease;
}
.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
